// components/views/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase';
import DailyDiscoveryService from '../../services/dailyDiscovery';
import AdminLogin from './AdminLogin';
import '../../styles/admin.css';

const AdminDashboard = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(
        sessionStorage.getItem('admin_authenticated') === 'true'
    );
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [discoveryStats, setDiscoveryStats] = useState({ count: 0, latest: null });
    const [generatingDiscovery, setGeneratingDiscovery] = useState(false);
    
    // Gestione lista daily
    const [dailyList, setDailyList] = useState([]);
    const [dailyLoading, setDailyLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [editingDaily, setEditingDaily] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({ title_it: '', title_en: '', introduction_it: '', introduction_en: '', discovery_date: '' });
    const itemsPerPage = 10;

    // Carica dati
    useEffect(() => {
        if (isAuthenticated) {
            loadUsers();
            loadDiscoveryStats();
            loadDailyList();
        }
    }, [isAuthenticated, currentPage]);

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            
            console.log('[AdminDashboard] Loading users...');
            
            // Verifica se c'è una sessione Supabase attiva
            const { data: { session } } = await supabase.auth.getSession();
            console.log('[AdminDashboard] Supabase session:', session ? 'exists' : 'none');
            
            // Prova prima con la funzione sicura (se disponibile)
            try {
                const { data: secureData, error: secureError } = await supabase
                    .rpc('get_all_users_with_favorites_secure');

                if (!secureError && secureData) {
                    console.log('[AdminDashboard] Users loaded via secure function:', secureData.length);
                    setUsers(secureData);
                    return;
                } else {
                    console.warn('[AdminDashboard] Secure function not available, fallback to legacy function:', secureError);
                }
            } catch (secureErr) {
                console.warn('[AdminDashboard] Secure function failed, trying legacy function:', secureErr);
            }

            // Prova poi con la funzione legacy che bypassa RLS (se esiste)
            try {
                const { data: functionData, error: functionError } = await supabase
                    .rpc('get_all_users_with_favorites');
                
                if (!functionError && functionData) {
                    console.log('[AdminDashboard] Users loaded via function:', functionData.length);
                    setUsers(functionData);
                    return;
                } else {
                    console.warn('[AdminDashboard] Function not available, falling back to direct query:', functionError);
                }
            } catch (funcErr) {
                console.warn('[AdminDashboard] Function call failed, trying direct query:', funcErr);
            }
            
            // Fallback: query diretta (richiede policy RLS appropriata)
            const { data: usersData, error: usersError } = await supabase
                .from('users')
                .select('user_id, created_at');

            if (usersError) {
                console.error('[AdminDashboard] Error loading users:', usersError);
                // Se è un errore di permessi RLS, mostra un messaggio più chiaro
                if (usersError.code === 'PGRST301' || usersError.message?.includes('permission') || usersError.message?.includes('policy')) {
                    throw new Error('Errore permessi: Le RLS policies bloccano l\'accesso. Esegui la migration add-admin-policies.sql in Supabase per abilitare l\'accesso admin.');
                }
                throw usersError;
            }
            
            console.log('[AdminDashboard] Users loaded:', usersData?.length || 0);

            // Per ogni utente, conta i preferiti
            const usersWithFavorites = await Promise.all(
                (usersData || []).map(async (user) => {
                    const { count, error: countError } = await supabase
                        .from('artist_favorites')
                        .select('*', { count: 'exact', head: true })
                        .eq('user_id', user.user_id);

                    if (countError) {
                        console.warn(`[AdminDashboard] Error counting favorites for user ${user.user_id}:`, countError);
                    }

                    return {
                        user_id: user.user_id,
                        created_at: user.created_at,
                        favorites_count: countError ? 0 : (count || 0)
                    };
                })
            );

            console.log('[AdminDashboard] Users with favorites:', usersWithFavorites.length);
            setUsers(usersWithFavorites);
        } catch (err) {
            console.error('Failed to load users:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadDiscoveryStats = async () => {
        try {
            // Prova funzione sicura admin first
            try {
                const { data: secureStats, error: secureStatsError } = await supabase
                    .rpc('admin_get_daily_stats');

                if (!secureStatsError && Array.isArray(secureStats) && secureStats.length > 0) {
                    const row = secureStats[0];
                    setDiscoveryStats({
                        count: row.total_count || 0,
                        latest: row.latest_id ? {
                            id: row.latest_id,
                            discovery_date: row.latest_discovery_date,
                            artist_count: row.latest_artist_count
                        } : null
                    });
                    return;
                }
            } catch (secureErr) {
                console.warn('[AdminDashboard] Secure stats function failed, using legacy query:', secureErr);
            }

            // Conta totale discoveries
            const { count, error: countError } = await supabase
                .from('daily_discoveries')
                .select('*', { count: 'exact', head: true });

            if (countError) throw countError;

            // Prendi ultima discovery
            const { data: latest, error: latestError } = await supabase
                .from('daily_discoveries')
                .select('*')
                .order('discovery_date', { ascending: false })
                .limit(1)
                .single();

            if (latestError && latestError.code !== 'PGRST116') throw latestError;

            setDiscoveryStats({ 
                count: count || 0, 
                latest: latest || null 
            });
        } catch (err) {
            console.error('Failed to load discovery stats:', err);
        }
    };

    const handleGenerateDiscovery = async () => {
        if (!window.confirm('Generare un nuovo Daily Discovery?')) {
            return;
        }

        setGeneratingDiscovery(true);
        setError(null);

        try {
            const discovery = await DailyDiscoveryService.generateTodayDiscovery();
            
            if (discovery) {
                alert(`✅ Discovery generata con successo!\n\nArtisti: ${discovery.artists?.length || 0}\nData: ${discovery.discovery_date}`);
                loadDiscoveryStats();
            } else {
                setError('Failed to generate discovery.');
            }
        } catch (err) {
            console.error('Error generating discovery:', err);
            setError(`Errore: ${err.message}`);
        } finally {
            setGeneratingDiscovery(false);
        }
    };

    const handleDeleteLastDiscovery = async () => {
        if (!window.confirm('⚠️ Eliminare l\'ultimo approfondimento generato?\n\nQuesta azione è irreversibile!')) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            try {
                const { data: deletedId, error: secureDeleteError } = await supabase
                    .rpc('admin_delete_last_daily_discovery');

                if (!secureDeleteError) {
                    if (!deletedId) {
                        setError('Nessun approfondimento da eliminare');
                        return;
                    }
                    alert(`✅ Eliminato con successo!`);
                    loadDiscoveryStats();
                    loadDailyList();
                    return;
                }
            } catch (secureDeleteErr) {
                console.warn('[AdminDashboard] Secure delete-last failed, using legacy delete:', secureDeleteErr);
            }

            const { data: lastDiscovery, error: fetchError } = await supabase
                .from('daily_discoveries')
                .select('id, title_en, title_it')
                .order('discovery_date', { ascending: false })
                .limit(1)
                .single();

            if (fetchError || !lastDiscovery) {
                setError('Nessun approfondimento da eliminare');
                return;
            }

            const { error: deleteError } = await supabase
                .from('daily_discoveries')
                .delete()
                .eq('id', lastDiscovery.id);

            if (deleteError) {
                throw deleteError;
            }

            alert(`✅ Eliminato con successo!`);
            loadDiscoveryStats();
            loadDailyList();
        } catch (err) {
            console.error('Error deleting discovery:', err);
            setError(`Failed to delete: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const loadDailyList = async () => {
        setDailyLoading(true);
        try {
            // Conta totale
            const { count, error: countError } = await supabase
                .from('daily_discoveries')
                .select('*', { count: 'exact', head: true });

            if (countError) throw countError;

            const total = count || 0;
            setTotalPages(Math.ceil(total / itemsPerPage));

            // Carica pagina
            const from = (currentPage - 1) * itemsPerPage;
            const to = from + itemsPerPage - 1;

            try {
                const { data: secureList, error: secureListError } = await supabase
                    .rpc('admin_list_daily_discoveries', {
                        p_offset: from,
                        p_limit: itemsPerPage
                    });

                if (!secureListError && Array.isArray(secureList)) {
                    setDailyList(secureList);
                    return;
                }
            } catch (secureListErr) {
                console.warn('[AdminDashboard] Secure list function failed, using legacy list query:', secureListErr);
            }

            const { data, error } = await supabase
                .from('daily_discoveries')
                .select('id, title_it, title_en, introduction_it, introduction_en, discovery_date, theme_category, artist_count')
                .order('discovery_date', { ascending: false })
                .range(from, to);

            if (error) throw error;

            setDailyList(data || []);
        } catch (err) {
            console.error('Error loading daily list:', err);
            setError(`Errore caricamento lista: ${err.message}`);
        } finally {
            setDailyLoading(false);
        }
    };

    const handleDeleteDaily = async (id, title) => {
        if (!window.confirm(`⚠️ Eliminare "${title}"?\n\nQuesta azione è irreversibile!`)) {
            return;
        }

        try {
            try {
                const { error: secureDeleteError } = await supabase
                    .rpc('admin_delete_daily_discovery', { p_id: id });

                if (!secureDeleteError) {
                    alert('✅ Eliminato con successo!');
                    loadDailyList();
                    loadDiscoveryStats();
                    return;
                }
            } catch (secureDeleteErr) {
                console.warn('[AdminDashboard] Secure delete failed, using legacy delete:', secureDeleteErr);
            }

            // Elimina prima gli artisti associati
            const { error: artistsError } = await supabase
                .from('daily_discovery_artists')
                .delete()
                .eq('discovery_id', id);

            if (artistsError) throw artistsError;

            // Poi elimina la discovery
            const { error: deleteError } = await supabase
                .from('daily_discoveries')
                .delete()
                .eq('id', id);

            if (deleteError) throw deleteError;

            alert('✅ Eliminato con successo!');
            loadDailyList();
            loadDiscoveryStats();
        } catch (err) {
            console.error('Error deleting daily:', err);
            alert(`Errore: ${err.message}`);
        }
    };

    const handleEditDaily = (daily) => {
        setEditingDaily(daily.id);
        setEditForm({
            title_it: daily.title_it || '',
            title_en: daily.title_en || '',
            introduction_it: daily.introduction_it || '',
            introduction_en: daily.introduction_en || '',
            discovery_date: daily.discovery_date || ''
        });
        setShowEditModal(true);
    };

    const handleSaveEdit = async () => {
        if (!editingDaily) return;

        try {
            try {
                const { error: secureUpdateError } = await supabase
                    .rpc('admin_update_daily_discovery', {
                        p_id: editingDaily,
                        p_title_it: editForm.title_it,
                        p_title_en: editForm.title_en,
                        p_introduction_it: editForm.introduction_it,
                        p_introduction_en: editForm.introduction_en,
                        p_discovery_date: editForm.discovery_date
                    });

                if (!secureUpdateError) {
                    alert('✅ Modificato con successo!');
                    handleCloseEditModal();
                    loadDailyList();
                    return;
                }
            } catch (secureUpdateErr) {
                console.warn('[AdminDashboard] Secure update failed, using legacy update:', secureUpdateErr);
            }

            const { error } = await supabase
                .from('daily_discoveries')
                .update({
                    title_it: editForm.title_it,
                    title_en: editForm.title_en,
                    introduction_it: editForm.introduction_it,
                    introduction_en: editForm.introduction_en,
                    discovery_date: editForm.discovery_date
                })
                .eq('id', editingDaily);

            if (error) throw error;

            alert('✅ Modificato con successo!');
            handleCloseEditModal();
            loadDailyList();
        } catch (err) {
            console.error('Error saving edit:', err);
            alert(`Errore: ${err.message}`);
        }
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditingDaily(null);
        setEditForm({ title_it: '', title_en: '', introduction_it: '', introduction_en: '', discovery_date: '' });
    };

    // Check autenticazione
    if (!isAuthenticated) {
        return <AdminLogin onLogin={() => setIsAuthenticated(true)} />;
    }

    return (
        <div className="admin-dashboard">
            <div className="admin-header">
                <img src="/img/logo.svg" alt="Foundly" className="admin-logo" />
            </div>

            <div className="admin-content">
                {/* Statistica Utenti */}
                <div className="admin-section">
                    <h2 className="admin-section-title">Utenti Registrati</h2>
                    {loading ? (
                        <p className="admin-loading">Caricamento...</p>
                    ) : error ? (
                        <p className="admin-error">Errore: {error}</p>
                    ) : (
                        <div className="admin-stat-item">
                            <span className="admin-stat-label">Totale Utenti</span>
                            <span className="admin-stat-value">{users.length}</span>
                        </div>
                    )}
                </div>

                {/* Pannello Daily Discovery */}
                <div className="admin-section">
                    <h2 className="admin-section-title">Daily Discovery</h2>
                    <div className="admin-discovery-stats">
                        <div className="admin-stat-item">
                            <span className="admin-stat-label">Totale Discoveries</span>
                            <span className="admin-stat-value">{discoveryStats.count}</span>
                        </div>
                        {discoveryStats.latest && (
                            <div className="admin-stat-item">
                                <span className="admin-stat-label">Ultima Discovery</span>
                                <span className="admin-stat-value-small">
                                    {new Date(discoveryStats.latest.discovery_date).toLocaleDateString()} • {discoveryStats.latest.artist_count} artisti
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="admin-discovery-actions">
                        <button 
                            className="admin-button admin-button-primary"
                            onClick={handleGenerateDiscovery}
                            disabled={generatingDiscovery}
                        >
                            {generatingDiscovery ? 'Generazione...' : 'Genera Nuova Discovery'}
                        </button>
                        <button 
                            className="admin-button admin-button-danger"
                            onClick={handleDeleteLastDiscovery}
                            disabled={loading}
                        >
                            {loading ? 'Eliminazione...' : 'Elimina Ultima Discovery'}
                        </button>
                    </div>
                </div>

                <div className="admin-section">
                    <h2 className="admin-section-title">Gestione Daily Discoveries</h2>
                    {dailyLoading ? (
                        <p className="admin-loading">Caricamento...</p>
                    ) : dailyList.length === 0 ? (
                        <p className="admin-empty">Nessun daily discovery</p>
                    ) : (
                        <>
                            <div className="admin-table-wrapper">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Data</th>
                                            <th>Titolo IT</th>
                                            <th>Titolo EN</th>
                                            <th>Azioni</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dailyList.map((daily) => (
                                            <tr key={daily.id}>
                                                <td data-label="Data">{new Date(daily.discovery_date).toLocaleDateString()}</td>
                                                <td data-label="Titolo IT">{daily.title_it || '-'}</td>
                                                <td data-label="Titolo EN">{daily.title_en || '-'}</td>
                                                <td>
                                                    <button
                                                        className="admin-button admin-button-small admin-button-primary"
                                                        onClick={() => handleEditDaily(daily)}
                                                    >
                                                        Modifica
                                                    </button>
                                                    <button
                                                        className="admin-button admin-button-small admin-button-danger"
                                                        onClick={() => handleDeleteDaily(daily.id, daily.title_it || daily.title_en)}
                                                        style={{ marginLeft: '5px' }}
                                                    >
                                                        Elimina
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* Paginazione */}
                            {totalPages > 1 && (
                                <div className="admin-pagination">
                                    <button
                                        className="admin-button admin-button-small"
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                    >
                                        ← Precedente
                                    </button>
                                    <span className="admin-page-info">
                                        Pagina {currentPage} di {totalPages}
                                    </span>
                                    <button
                                        className="admin-button admin-button-small"
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                    >
                                        Successiva →
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Modal modifica */}
            {showEditModal && (
                <div className="admin-modal-overlay" onClick={handleCloseEditModal}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h2 className="admin-modal-title">Modifica Daily Discovery</h2>
                            <button 
                                className="admin-modal-close"
                                onClick={handleCloseEditModal}
                            >
                                ×
                            </button>
                        </div>
                        <div className="admin-modal-content">
                            <div className="admin-form-group">
                                <label className="admin-form-label">Data</label>
                                <input
                                    type="date"
                                    value={editForm.discovery_date}
                                    onChange={(e) => setEditForm({ ...editForm, discovery_date: e.target.value })}
                                    className="admin-input"
                                />
                            </div>

                            <div className="admin-form-group">
                                <label className="admin-form-label">Titolo IT</label>
                                <input
                                    type="text"
                                    value={editForm.title_it}
                                    onChange={(e) => setEditForm({ ...editForm, title_it: e.target.value })}
                                    className="admin-input"
                                    placeholder="Titolo italiano"
                                />
                            </div>

                            <div className="admin-form-group">
                                <label className="admin-form-label">Titolo EN</label>
                                <input
                                    type="text"
                                    value={editForm.title_en}
                                    onChange={(e) => setEditForm({ ...editForm, title_en: e.target.value })}
                                    className="admin-input"
                                    placeholder="English title"
                                />
                            </div>

                            <div className="admin-form-group">
                                <label className="admin-form-label">Testo IT</label>
                                <textarea
                                    value={editForm.introduction_it}
                                    onChange={(e) => setEditForm({ ...editForm, introduction_it: e.target.value })}
                                    className="admin-textarea"
                                    rows="10"
                                    placeholder="Testo italiano..."
                                />
                            </div>

                            <div className="admin-form-group">
                                <label className="admin-form-label">Testo EN</label>
                                <textarea
                                    value={editForm.introduction_en}
                                    onChange={(e) => setEditForm({ ...editForm, introduction_en: e.target.value })}
                                    className="admin-textarea"
                                    rows="10"
                                    placeholder="English text..."
                                />
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button
                                className="admin-button"
                                onClick={handleCloseEditModal}
                            >
                                Annulla
                            </button>
                            <button
                                className="admin-button admin-button-primary"
                                onClick={handleSaveEdit}
                            >
                                Salva Modifiche
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
